'use client';

/**
 * NutritionSynergyCard — joins per-day nutrition with the next day's
 * session quality, exposing patterns the Action Points engine fires
 * on (so the user can drill down to see the underlying data).
 *
 * Three sub-sections inside the card:
 *
 *   1. Protein per lb of bodyweight — the absolute level read, framed
 *      against the 0.7/0.9/1.0 g/lb thresholds. Always shown when we
 *      have enough nutrition + bodyweight data.
 *
 *   2. Protein response — side-by-side bars showing next-session
 *      quality on "hit" days (>=90% goal) vs "miss" days (<70% goal).
 *      Surfaces the delta with a confidence-style chip.
 *
 *   3. Calorie zones — quality across deep-deficit / mild-deficit /
 *      adequate / surplus bins. Highlights the user's best + worst
 *      zone with a colored ring.
 *
 * Each sub-section hides cleanly when its data isn't sufficient,
 * rather than rendering a misleading "no data" emptyness.
 */

import { Apple, Beef } from 'lucide-react';
import OpsPanel from './OpsPanel';
import type {
  NutritionTrainingSynergy,
  ProteinResponseSummary,
  CalorieResponseSummary,
  ProteinRatioSummary,
  CalorieBin,
} from '@/lib/nutritionTrainingSynergy';

interface Props {
  summary: NutritionTrainingSynergy | null;
}

export default function NutritionSynergyCard({ summary }: Props) {
  if (!summary) {
    return (
      <OpsPanel badge="Nutrition × Training" code="SYS·NUT·02" accent="amber"
        callout={<span className="text-slate-500">LEARNING</span>}>
        <div className="flex items-start gap-2">
          <Apple className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
          <p className="text-[12px] text-slate-400 leading-snug">
            Log food consistently for 2+ weeks AND keep logging sessions — the engine learns whether nutrition choices actually move your session quality. We won't fake a correlation that isn't there.
          </p>
        </div>
      </OpsPanel>
    );
  }

  const { protein, calorie, ratio } = summary;
  const anyDataset =
    ratio.status !== 'unknown' ||
    protein.sufficient ||
    calorie.sufficient;

  if (!anyDataset) {
    return (
      <OpsPanel badge="Nutrition × Training" code="SYS·NUT·02" accent="amber"
        callout={<span className="text-slate-500">SPARSE</span>}>
        <p className="text-[12px] text-slate-400 leading-snug">
          Not enough paired data yet — each pattern needs at least 5 days in each bin (e.g. 5 hit-protein days AND 5 miss-protein days) before we can call it.
        </p>
        <p className="font-mono text-[9px] text-slate-600 mt-2 uppercase tracking-[0.18em] tabular-nums">
          {summary.sessionsAnalyzed} sessions · {summary.daysAnalyzed} nutrition days
        </p>
      </OpsPanel>
    );
  }

  return (
    <OpsPanel badge="Nutrition × Training" code="SYS·NUT·02" accent="amber">
      {ratio.status !== 'unknown' && <ProteinRatioRow ratio={ratio} />}
      {protein.sufficient && <ProteinResponseRow protein={protein} />}
      {calorie.sufficient && <CalorieResponseRow calorie={calorie} />}
      <div className="mt-3 pt-2 border-t border-slate-800/60 font-mono text-[9px] text-slate-600 uppercase tracking-[0.18em] tabular-nums">
        {summary.sessionsAnalyzed} paired sessions · {summary.daysAnalyzed} nutrition days
      </div>
    </OpsPanel>
  );
}

function ProteinRatioRow({ ratio }: { ratio: ProteinRatioSummary }) {
  const statusTw = ratio.status === 'optimal' ? 'text-emerald-400'
    : ratio.status === 'adequate' ? 'text-amber-400'
    : ratio.status === 'low' ? 'text-red-400' : 'text-slate-400';
  const statusLabel = ratio.status === 'optimal' ? 'OPTIMAL'
    : ratio.status === 'adequate' ? 'ADEQUATE'
    : ratio.status === 'low' ? 'LOW' : 'UNKNOWN';

  // Visual: tick marks at 0.7 and 0.9 g/lb against a 0 → 1.2 g/lb scale.
  const SCALE_MAX = 1.2;
  const pct = (v: number) => Math.max(0, Math.min(100, (v / SCALE_MAX) * 100));
  const pipPct = ratio.gPerLb !== null ? pct(ratio.gPerLb) : 0;

  return (
    <div className="mb-3">
      <div className="flex items-baseline justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <Beef className="w-3 h-3 text-amber-400" />
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-amber-400">
            Protein × Bodyweight
          </span>
        </div>
        <span className={`font-mono text-[10px] uppercase tracking-[0.18em] font-bold ${statusTw}`}>
          {statusLabel}
        </span>
      </div>
      <div className="rounded-md border border-slate-800/80 bg-slate-900/40 px-3 py-2">
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <span className="font-heading text-2xl font-bold tabular-nums text-white">
              {ratio.gPerLb !== null ? ratio.gPerLb.toFixed(2) : '—'}
            </span>
            <span className="font-mono text-[9px] text-slate-500 uppercase tracking-[0.14em] ml-1">g / lb</span>
          </div>
          <div className="text-right font-mono text-[9px] text-slate-500 uppercase tracking-[0.16em] tabular-nums">
            <div>{ratio.avgProteinG > 0 ? `${ratio.avgProteinG.toFixed(0)}g avg` : '—'}</div>
            {ratio.bodyweightLbs && <div>{ratio.bodyweightLbs.toFixed(0)} lb bw</div>}
          </div>
        </div>
        <div className="relative h-1.5 rounded-sm bg-slate-900 border border-slate-800 overflow-hidden">
          {/* 0.7 floor tick */}
          <span aria-hidden className="absolute top-0 bottom-0 w-px bg-amber-500/50" style={{ left: `${pct(0.7)}%` }} />
          {/* 0.9 optimal threshold */}
          <span aria-hidden className="absolute top-0 bottom-0 w-px bg-emerald-500/60" style={{ left: `${pct(0.9)}%` }} />
          {/* Pip */}
          {ratio.gPerLb !== null && (
            <span
              className={`absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full -translate-x-1/2 shadow-[0_0_4px_currentColor] ${
                ratio.status === 'optimal' ? 'bg-emerald-400'
                  : ratio.status === 'adequate' ? 'bg-amber-400' : 'bg-red-500'
              }`}
              style={{ left: `${pipPct}%` }}
            />
          )}
        </div>
        <div className="flex items-baseline justify-between mt-0.5 font-mono text-[8px] uppercase tracking-[0.14em] text-slate-600 tabular-nums">
          <span>0.0</span>
          <span>0.7 floor</span>
          <span>0.9 optimal</span>
          <span>1.2</span>
        </div>
      </div>
    </div>
  );
}

function ProteinResponseRow({ protein }: { protein: ProteinResponseSummary }) {
  // Two side-by-side vertical bars showing avg quality on hit vs miss
  // protein days. Quality is 0-1; scale the bar height to that.
  const hitPct = Math.round(protein.hitMean * 100);
  const missPct = Math.round(protein.missMean * 100);
  const deltaPct = Math.round(protein.delta * 100);
  const positive = protein.delta > 0;
  const deltaTw = !protein.meaningful ? 'text-slate-500'
    : positive ? 'text-emerald-400' : 'text-red-400';
  const verdict = !protein.meaningful
    ? "No clear protein effect detected in your data yet. Either you're consistent in both bins (good problem to have) or we need more samples."
    : positive
      ? `Days where you hit protein are followed by sessions ${Math.abs(deltaPct)} quality points higher. Lock the floor in.`
      : `Your data shows a slight reverse pattern — likely a sample-size artifact, not a real effect. Keep logging.`;

  return (
    <div className="mb-3">
      <div className="flex items-baseline justify-between mb-1">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-amber-400">
          Protein Response
        </span>
        <span className={`font-mono text-[10px] uppercase tracking-[0.18em] font-bold tabular-nums ${deltaTw}`}>
          {protein.meaningful ? `${positive ? '+' : ''}${deltaPct} pts` : 'no effect'}
        </span>
      </div>
      <div className="rounded-md border border-slate-800/80 bg-slate-900/40 px-3 py-2">
        <div className="grid grid-cols-2 gap-2">
          <ProteinBar label="Hit (≥90% goal)" pct={hitPct} samples={protein.hitSamples} tw="bg-emerald-400" />
          <ProteinBar label="Miss (<70% goal)" pct={missPct} samples={protein.missSamples} tw="bg-amber-400" />
        </div>
        <p className="text-[11px] text-slate-300 leading-snug mt-2">{verdict}</p>
      </div>
    </div>
  );
}

function ProteinBar({ label, pct, samples, tw }: { label: string; pct: number; samples: number; tw: string }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] text-slate-300">{label}</span>
        <span className="font-mono text-[10px] text-slate-300 tabular-nums">{pct}%</span>
      </div>
      <div className="relative h-2 rounded-sm bg-slate-900 border border-slate-800 overflow-hidden">
        <span className={`absolute inset-y-0 left-0 ${tw}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="font-mono text-[8px] text-slate-600 uppercase tracking-[0.14em] mt-0.5 tabular-nums">
        n={samples}
      </div>
    </div>
  );
}

const BIN_LABEL: Record<CalorieBin, string> = {
  'deep-deficit': 'Deep deficit',
  'mild-deficit': 'Mild deficit',
  'adequate': 'Adequate',
  'surplus': 'Surplus',
};

const BIN_TW: Record<CalorieBin, string> = {
  'deep-deficit': 'bg-red-500/70',
  'mild-deficit': 'bg-amber-400',
  'adequate':    'bg-emerald-400',
  'surplus':     'bg-cyan-400',
};

function CalorieResponseRow({ calorie }: { calorie: CalorieResponseSummary }) {
  // Show every bin we have, but dim those with too few samples.
  const order: CalorieBin[] = ['deep-deficit', 'mild-deficit', 'adequate', 'surplus'];
  return (
    <div className="mb-1">
      <div className="flex items-baseline justify-between mb-1">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-amber-400">
          Calorie Zones
        </span>
        {calorie.bestBin && (
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-400 tabular-nums">
            BEST · {BIN_LABEL[calorie.bestBin]}
          </span>
        )}
      </div>
      <div className="rounded-md border border-slate-800/80 bg-slate-900/40 px-3 py-2">
        <div className="space-y-1.5">
          {order.map(bin => {
            const r = calorie.bins.find(b => b.bin === bin);
            if (!r) return null;
            const dim = r.samples < 5;
            const pct = Math.round(r.meanQuality * 100);
            return (
              <div key={bin} className={dim ? 'opacity-40' : ''}>
                <div className="flex items-baseline justify-between mb-0.5">
                  <span className="text-[10px] text-slate-300">{BIN_LABEL[bin]}</span>
                  <span className="font-mono text-[9px] text-slate-500 uppercase tracking-[0.14em] tabular-nums">
                    {r.samples > 0 ? `${pct}% · n=${r.samples}` : 'no data'}
                  </span>
                </div>
                <div className="relative h-1.5 rounded-sm bg-slate-900 border border-slate-800 overflow-hidden">
                  <span className={`absolute inset-y-0 left-0 ${BIN_TW[bin]}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
        {calorie.worstBin && calorie.bestBin && calorie.worstBin !== calorie.bestBin && (
          <p className="text-[11px] text-slate-300 leading-snug mt-2">
            Your sessions go best on <span className="text-emerald-400 font-semibold">{BIN_LABEL[calorie.bestBin].toLowerCase()}</span> days
            and worst on <span className="text-red-400 font-semibold">{BIN_LABEL[calorie.worstBin].toLowerCase()}</span> days. Bank kcal accordingly.
          </p>
        )}
      </div>
    </div>
  );
}
